// React, store and 3rd party
import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import { Stack, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import LoadingButton from '@mui/lab/LoadingButton';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';

import { ACTIONS } from '@components/PropertiesForm/PropertiesForm';
import ConfirmationDialog from '@components/ConfirmationDialog';

// Store
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import { usePermissions } from '@store/auth';
import {
  getLOBMethodsByCurrencyAndPlatform,
  getMerchantTabPriority,
  paymentOptionsMerchantsActions,
  selectSelectedMerchant,
  selectSelectMerchantMethods,
} from '../../store/payment-options-merchants';

import DraggableTabsList from '../../components/DraggableTabList';
import GatewayPropertiesFormModal from '../gateway-properties/GatewayPropertiesFormModal';
import { updateGatewayMapping } from '../../store/payment-options-gateways';
import { selectPlatformsWithChannelHash } from '../../store/payment-options-platforms';
import {
  getTabs,
  selectTabsDefaultPriority,
} from '../../store/payment-options-tabs';
import { selectCurrencies } from '../../store/payment-options-currencies';

const MSG_ADD_METHOD_SUCCESS =
  'Selected methods are successfully added. Refreshing data...';

/**
 *  Component that displays payment methods of an LOB.
 */
function LOBPaymentMethods({
  onTabOrderChange = () => {},
  submitError,
  submitSuccess,
  resetTabOrder,
}) {
  const dispatch = useDispatch();
  const permissions = usePermissions();

  const tabOrderChanged = useRef(false);

  const [isFetchingMethods, setIsFetchingMethods] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('MYR');
  const [selectedPlatform, setSelectedPlatform] = useState('web');
  const [selectedTab, setSelectedTab] = useState();
  const [tabMethods, setTabMethods] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [showHiddenTabs, setShowHiddenTabs] = useState(false);

  // Properties Form
  const [selectedMethod, setSelectedMethod] = useState(ACTIONS.ADD);
  const [propertiesFormAction, setPropertiesFormAction] = useState(ACTIONS.ADD);
  const [showPropertiesFormModal, setShowPropertiesFormModal] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const selectedLOB = useSelector(selectSelectedMerchant);
  const merchantMethodsByTabs = useSelector(selectSelectMerchantMethods);
  const currencies = useSelector(selectCurrencies);
  const platforms = useSelector(
    selectPlatformsWithChannelHash(selectedLOB.merchantDetails),
  );

  const tabsDefaultPriority = useSelector(selectTabsDefaultPriority);

  const [
    showStatusChangeConfirmationDialog,
    setShowStatusChangeConfirmationDialog,
  ] = useState({ show: false });
  const [
    selectedMethodDetailsForStatusChange,
    setSelectedMethodDetailsForStatusChange,
  ] = useState(null);

  useEffect(() => {
    dispatch(
      paymentOptionsMerchantsActions.setMerchantTabWithDefault(
        tabsDefaultPriority,
      ),
    );
  }, [dispatch, tabsDefaultPriority]);

  const fetchMerchantMethods = useCallback(() => {
    if (
      !selectedLOB ||
      !selectedCurrency ||
      !selectedPlatform ||
      isFetchingMethods
    )
      return;

    setSuccessMessage('');
    setErrorMessage('');
    setIsFetchingMethods(true);

    dispatch(
      getLOBMethodsByCurrencyAndPlatform({
        id: selectedLOB.lobId,
        currency: selectedCurrency,
        platform: selectedPlatform,
      }),
    )
      .then((resp) => {
        if (resp.payload.status !== 'SUCCESS') {
          // TODO: perform tab prio here
          return;
        }

        const selectedMerchant = selectedLOB.merchantDetails.find(
          (p) => p.platformCode === selectedPlatform,
        );

        if (selectedMerchant) {
          dispatch(getMerchantTabPriority(selectedMerchant?.merchantId)).then(
            ({ payload }) => {
              if (payload?.status !== 'SUCCESS') {
                dispatch(getTabs());
              }
            },
          );
        }

        if (resp?.payload?.message) setErrorMessage(resp.payload.message);
      })
      .finally(() => {
        setIsFetchingMethods(false);
      });
  }, [
    dispatch,
    isFetchingMethods,
    selectedCurrency,
    selectedLOB,
    selectedPlatform,
  ]);

  const onMethodTabChange = (event, tabId) => {
    const newTab = selectedLOB.tabs.find((m) => m.id === tabId);

    setSelectedTab(newTab);
  };

  const onPlatformChange = useCallback((event) => {
    setSuccessMessage('');
    setErrorMessage('');
    setSelectedPlatform(event.target.value);
  }, []);

  const onCurrencyChange = useCallback((event) => {
    setSuccessMessage('');
    setErrorMessage('');
    setSelectedCurrency(event.target.value);
  }, []);

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) return;

    const newTabs = Array.from(tabs);
    const draggedTab = newTabs.splice(result.source.index, 1)[0];

    newTabs.splice(result.destination.index, 0, draggedTab);
    setTabs(newTabs);

    const isSameOrder = newTabs.every((tab, index) => {
      if (index + 1 < newTabs.length) {
        return tab.priority < newTabs[index + 1].priority;
      }
      return true;
    });

    tabOrderChanged.current = !isSameOrder;

    onTabOrderChange({
      isChanged: !isSameOrder,
      selectedPlatform: selectedLOB.merchantDetails.find(
        (p) => p.platformCode === selectedPlatform,
      ),
      tabOrder: newTabs,
      selectedCurrency,
    });
  };

  const onSubmitSuccess = useCallback(() => {
    dispatch(showSuccessMessage(MSG_ADD_METHOD_SUCCESS));
  }, [dispatch]);

  const onCloseDialog = useCallback(
    (refreshData) => {
      if (refreshData) fetchMerchantMethods();
      setPropertiesFormAction(ACTIONS.ADD);
      setShowPropertiesFormModal(false);
    },
    [fetchMerchantMethods],
  );

  useEffect(() => {
    setSuccessMessage(submitSuccess);
  }, [submitSuccess]);

  useEffect(() => {
    setErrorMessage(submitError);
  }, [submitError]);

  /**
   * Invoked on page load.
   */
  useEffect(() => {
    fetchMerchantMethods();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Temporary fix

  /**
   * Creates copy of selectedLOB tabs for sorting
   */
  useEffect(() => {
    const { tabs: merchantTabs } = selectedLOB;

    if (!merchantTabs || !merchantMethodsByTabs) return;

    let newTabs = merchantTabs.map((t) => ({
      ...t,
      disabled:
        merchantMethodsByTabs.length > 0
          ? merchantMethodsByTabs.findIndex((m) => m.tabId === t.id) < 0
          : false,
    }));

    newTabs = newTabs.sort((a, b) => a.priority - b.priority);

    setTabs(newTabs);
  }, [selectedLOB, merchantMethodsByTabs]);

  /**
   * Invoked when parent reset button is clicked
   */
  useEffect(() => {
    if (resetTabOrder && tabOrderChanged.current) {
      const origTabs = [...tabs];

      origTabs.sort((a, b) => a.priority - b.priority);
      setTabs(origTabs);
    }
  }, [resetTabOrder, tabs]);

  /**
   * Invoked after fetching merchant methods.
   */
  useEffect(() => {
    if (!merchantMethodsByTabs || merchantMethodsByTabs.length === 0) return;
    if (!selectedLOB.tabs || selectedLOB.tabs.length === 0) return;

    const firstMerchantMethodTabId = merchantMethodsByTabs[0]?.tabId;
    const firstEnabledTab = selectedLOB.tabs.find(
      (m) => m.id === firstMerchantMethodTabId,
    );

    setSelectedTab(firstEnabledTab);
  }, [selectedLOB, merchantMethodsByTabs]);

  /**
   * Called on tab click.
   */
  useEffect(() => {
    if (!selectedTab) return;

    if (merchantMethodsByTabs.length === 0) {
      setTabMethods([]);
      return;
    }

    const tab = merchantMethodsByTabs.find((m) => m.tabId === selectedTab.id);
    const parentMethods = tab?.channels;

    if (!tab || !parentMethods) return;

    const subMethods = parentMethods?.reduce((prev, curr) => {
      const subChannels = curr.subChannels || [];
      return [...prev, ...subChannels];
    }, []);
    const list = [...parentMethods, ...subMethods];

    if (tab) setTabMethods(list);
  }, [selectedTab, merchantMethodsByTabs]);

  const handleStatusChange = (row) => {
    const { gatewayId, methodId, mappingStatus } = row;
    setSelectedMethodDetailsForStatusChange({
      gatewayId,
      methodId,
      mappingStatus,
    });

    setShowStatusChangeConfirmationDialog({ show: true });
  };

  const onStatusChangeConfirmation = ({
    gatewayId,
    methodId,
    mappingStatus,
  }) => {
    const { merchantId } = selectedLOB.merchantDetails.find(
      (merchant) => merchant.platformCode === selectedPlatform,
    );
    const { id: currencyId } = currencies.find(
      (c) => c.code === selectedCurrency,
    );

    const payload = {
      merchantIds: [merchantId],
      currencyIds: [currencyId],
      methodIds: [methodId],
      gatewayId,
      status: mappingStatus === 0 ? 1 : 0,
    };

    dispatch(updateGatewayMapping(payload)).then((data) => {
      if (data.payload && data.payload.status === 'SUCCESS') {
        dispatch(
          showSuccessMessage(
            'Yay! Payment method status has been updated successfully.',
          ),
        );

        fetchMerchantMethods();
      } else {
        dispatch(
          showErrorMessage({
            message:
              'Oops! Something went wrong while updating the payment method status.',
            duration: 3000,
          }),
        );
      }
    });
  };

  return (
    <Grid
      container
      spacing={2}>
      {errorMessage && (
        <Grid
          item
          xs={12}>
          <Alert
            onClose={() => {
              setErrorMessage('');
            }}
            sx={{ marginBottom: '10px' }}
            severity="error">
            {errorMessage}
          </Alert>
        </Grid>
      )}
      {successMessage && (
        <Grid
          item
          xs={12}>
          <Alert
            onClose={() => {
              setSuccessMessage('');
            }}
            sx={{ marginBottom: '10px' }}
            severity="success">
            {successMessage}
          </Alert>
        </Grid>
      )}
      <Grid
        item
        md={4}
        xs={6}>
        <FormControl
          fullWidth
          size="small">
          <InputLabel id="platform-label">Platform</InputLabel>
          <Select
            labelId="platform-label"
            value={selectedPlatform}
            label="Platform"
            onChange={onPlatformChange}>
            {platforms &&
              platforms.map((p) => (
                <MenuItem
                  key={p.code}
                  value={p.code}>
                  {p.code}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid
        item
        md={4}
        xs={6}>
        <FormControl
          fullWidth
          size="small">
          <InputLabel id="currency-label">Currency</InputLabel>
          <Select
            labelId="currency-label"
            value={selectedCurrency}
            label="Currency"
            onChange={onCurrencyChange}>
            {currencies &&
              currencies.map((c) => (
                <MenuItem
                  key={c.code}
                  value={c.code}>
                  {c.code}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid
        item
        md={2}
        xs={12}>
        <LoadingButton
          id="getMethodsBtn"
          variant="outlined"
          size="large"
          fullWidth
          onClick={fetchMerchantMethods}
          startIcon={<SearchIcon />}
          disabled={showHiddenTabs || !selectedCurrency || !selectedPlatform}
          loadingPosition="start"
          loading={isFetchingMethods}>
          Search
        </LoadingButton>
      </Grid>
      <Grid
        item
        md={2}
        xs={12}>
        <LoadingButton
          id="addMethodBtn"
          variant="contained"
          size="large"
          fullWidth
          startIcon={<AddIcon />}
          disabled={
            !permissions?.canEditPaymentOptions ||
            !selectedCurrency ||
            !selectedPlatform
          }
          onClick={() => {
            setShowPropertiesFormModal(true);
          }}>
          Add
        </LoadingButton>
      </Grid>
      {selectedTab && selectedTab.id >= 0 && (
        <Grid
          id="paymentMethodsWrapper"
          item
          container
          xs={12}>
          <Grid
            item
            container
            alignContent="space-between"
            style={{ minHeight: 200 }}
            xs={6}
            md={3}>
            <DraggableTabsList
              id="tabListWrapper"
              onDragEnd={onDragEnd}
              tabs={tabs}
              selected={selectedTab.id}
              showHiddenTabs={showHiddenTabs}
              onTabClick={(e, newTab) => onMethodTabChange(e, newTab)}
            />
            <FormControlLabel
              style={{ padding: '0 16px' }}
              control={
                <Switch
                  checked={showHiddenTabs}
                  onChange={() => setShowHiddenTabs(!showHiddenTabs)}
                />
              }
              label="Enable reordering of tabs"
            />
          </Grid>
          <Grid
            item
            style={{ minHeight: 418 }} // 10 rows
            xs={6}
            md={9}>
            <DataGrid
              id="methodListWrapper"
              style={{ background: 'white' }}
              rows={tabMethods}
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableDensitySelector
              onRowClick={({ row: method }) => {
                setSelectedMethod(method);
                setPropertiesFormAction(ACTIONS.EDIT);
                setShowPropertiesFormModal(true);
              }}
              getRowId={(row) => (row.methodId ? row.methodId : row.tabOrder)}
              columns={[
                {
                  field: 'methodId',
                  headerName: 'Method Id',
                  width: 100,
                  sortable: false,
                  headerAlign: 'center',
                  align: 'center',
                },
                {
                  field: 'methodCode',
                  headerName: 'Method Code',
                  sortable: false,
                  headerAlign: 'left',
                  align: 'left',
                  flex: 1,
                },
                {
                  field: 'methodName',
                  headerName: 'Method Name',
                  sortable: false,
                  headerAlign: 'left',
                  align: 'left',
                  flex: 1,
                },
                {
                  field: 'parentMethodId',
                  headerName: 'Parent Method',
                  sortable: false,
                  headerAlign: 'left',
                  align: 'left',
                  flex: 1,
                  valueGetter: ({ row }) => {
                    if (!row.parentMethodId) return null;

                    const parentMethod = tabMethods.find(
                      (method) => method.methodId === row.parentMethodId,
                    );
                    return parentMethod.methodName;
                  },
                },
                {
                  field: 'gatewayName',
                  headerName: 'Gateway Name',
                  sortable: false,
                  flex: 1,
                },
                {
                  field: 'action',
                  headerName: 'Status',
                  type: 'actions',
                  flex: 1,
                  getActions: ({ row }) => {
                    const isChecked = row.mappingStatus === 1;
                    const label = isChecked ? 'Enabled' : 'Disabled';
                    const color = isChecked ? 'green' : 'grey';

                    return [
                      <Stack
                        key={0}
                        flexDirection="column"
                        alignItems="center">
                        <Switch
                          color="success"
                          size="small"
                          disabled={!permissions?.canEditPaymentOptions}
                          checked={isChecked}
                          onChange={() => handleStatusChange(row)}
                        />
                        <Typography
                          color={color}
                          variant="caption">
                          {label}
                        </Typography>
                      </Stack>,
                    ];
                  },
                },
              ]}
              pageSize={20}
              rowsPerPageOptions={[10]}
            />
          </Grid>
        </Grid>
      )}
      {showPropertiesFormModal && (
        <GatewayPropertiesFormModal
          open={showPropertiesFormModal}
          action={propertiesFormAction}
          data={{
            lobName: selectedLOB.lobName,
            merchants: [
              selectedLOB.merchantDetails.find(
                (p) => p.platformCode === selectedPlatform,
              ),
            ],
            currencies: [selectedCurrency],
            methods:
              propertiesFormAction === ACTIONS.ADD &&
              merchantMethodsByTabs.length > 0
                ? merchantMethodsByTabs.reduce(
                    (prev, tab) => prev.concat(tab.channels),
                    [],
                  )
                : [selectedMethod],
          }}
          closeDialog={onCloseDialog}
          submitSuccessCallback={onSubmitSuccess}
        />
      )}
      <ConfirmationDialog
        openDialog={showStatusChangeConfirmationDialog}
        yesHandler={onStatusChangeConfirmation}
        data={selectedMethodDetailsForStatusChange}
        title="Update payment method status"
        message="Are you sure you want to update the status of the selected payment method?"
      />
    </Grid>
  );
}

export default LOBPaymentMethods;
